<template>
  <div
    class="min-h-screen bg-[#f4f7f6] pb-32 text-slate-800 font-sans antialiased"
  >
    <!-- SKELETON LOADING -->
    <div
      v-if="isLoading"
      class="mx-auto max-w-7xl px-4 py-12 space-y-6"
    >
      <div class="h-48 w-full animate-pulse rounded-3xl bg-slate-200/80"></div>
      <div class="h-96 w-full animate-pulse rounded-3xl bg-slate-200/80"></div>
    </div>

    <main
      v-else
      class="mx-auto max-w-[92rem] px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8"
    >
      <!-- TOP PAGE HERO BAR -->
      <div
        class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-3xl border border-slate-200/80 shadow-xs"
      >
        <div>
          <div
            class="flex items-center gap-2 text-xs font-bold text-emerald-700 tracking-wider uppercase mb-1"
          >
            <span
              class="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"
            ></span>
            Educanium Studio Workspace
          </div>
          <h1
            class="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight"
          >
            Header Branding & Navigation Studio
          </h1>
          <p class="text-xs sm:text-sm text-slate-500 mt-0.5">
            Design your school's live header interface with instant real-time
            canvas preview.
          </p>
        </div>

        <div class="flex items-center gap-3">
          <button
            type="button"
            class="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 transition active:scale-95 cursor-pointer shadow-xs"
            :disabled="isSaving"
            @click="confirmReset"
          >
            Discard Changes
          </button>
          <button
            type="button"
            class="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#0f2423] hover:bg-[#163533] text-white text-xs font-bold shadow-lg shadow-[#0f2423]/20 transition active:scale-95 cursor-pointer disabled:opacity-50"
            :disabled="isSaving"
            @click="handleSave"
          >
            <Loader2Icon
              v-if="isSaving"
              class="h-4 w-4 animate-spin text-[#d4af37]"
            />
            <SaveIcon
              v-else
              class="h-4 w-4 text-[#d4af37]"
            />
            <span>{{ isSaving ? "Saving Config..." : "Publish Header" }}</span>
          </button>
        </div>
      </div>

      <!-- MAIN SPLIT WORKSPACE GRID -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <!-- LEFT COLUMN: STICKY REAL-TIME PREVIEW CANVAS (5 Cols on LG) -->
        <div class="lg:col-span-5 lg:sticky lg:top-6 space-y-4">
          <div class="flex items-center justify-between px-1">
            <span
              class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2"
            >
              <EyeIcon class="h-4 w-4 text-emerald-600" />
              Live Canvas Preview
            </span>
            <span
              class="bg-emerald-100 text-emerald-800 text-[10px] font-extrabold px-2.5 py-0.5 rounded-full"
            >
              Synced
            </span>
          </div>

          <!-- The Rendered Header Mockup -->
          <div
            class="rounded-3xl border border-slate-300/80 bg-white shadow-xl shadow-slate-900/5 overflow-hidden transition-all duration-300 ring-4 ring-slate-900/5"
          >
            <!-- 1. Dark Top Bar -->
            <div
              class="bg-[#0f2423] text-[#e2e8f0] text-[11px] py-2.5 px-4 flex items-center justify-between gap-2 border-b border-[#1b3d3a]"
            >
              <div class="flex items-center gap-3 truncate font-medium">
                <span
                  v-if="form.city || form.address"
                  class="flex items-center gap-1 shrink-0"
                >
                  <MapPinIcon class="h-3 w-3 text-[#d4af37]" />
                  <span>{{ form.city || form.address }}</span>
                </span>
                <span
                  v-if="form.primaryPhone"
                  class="hidden sm:flex items-center gap-1 shrink-0"
                >
                  <PhoneIcon class="h-3 w-3 text-[#d4af37]" />
                  <span>{{ form.primaryPhone }}</span>
                </span>
              </div>

              <div class="flex items-center gap-2.5 shrink-0">
                <span
                  class="hidden sm:inline-block bg-[#1a3836] text-[#d4af37] px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-[#27534f]"
                >
                  ADMISSIONS OPEN
                </span>
                <div class="flex items-center gap-2 text-[#d4af37]">
                  <MailIcon
                    v-if="form.email"
                    class="h-3 w-3"
                  />
                  <FacebookIcon
                    v-if="form.facebook"
                    class="h-3 w-3"
                  />
                  <YoutubeIcon
                    v-if="form.youtube"
                    class="h-3 w-3"
                  />
                </div>
              </div>
            </div>

            <!-- 2. Main Brand Bar -->
            <div
              class="p-4 bg-[#fdfbf7] flex items-center justify-between gap-3 border-b border-slate-200"
            >
              <div class="flex items-center gap-3 min-w-0">
                <div
                  class="h-11 w-11 rounded-2xl bg-[#0f2423]/10 flex items-center justify-center border border-slate-200 overflow-hidden shrink-0 p-1"
                >
                  <img
                    v-if="resolvedLogoUrl"
                    :src="resolvedLogoUrl"
                    class="h-full w-full object-contain rounded-xl"
                    alt="Logo"
                  />
                  <span
                    v-else
                    class="font-serif font-black text-sm text-[#0f2423]"
                    >{{ schoolInitials }}</span
                  >
                </div>
                <div class="truncate">
                  <h2
                    class="text-base font-bold font-serif text-[#0f2423] truncate leading-tight"
                  >
                    {{ form.schoolName || "Vinoda Academy" }}
                  </h2>
                  <p
                    class="text-[11px] font-medium italic text-[#78613c] truncate"
                  >
                    {{ form.tagline || "Educanium International" }}
                  </p>
                </div>
              </div>

              <!-- Quick action mock buttons -->
              <div class="flex items-center gap-1.5 shrink-0">
                <span
                  class="px-3 py-1.5 rounded-xl bg-[#0f2423] text-white text-[10px] font-bold shadow-xs"
                >
                  ENQUIRE
                </span>
              </div>
            </div>

            <!-- 3. Mini Navigation Strip -->
            <div
              class="bg-[#0f2423] px-3 py-2 flex items-center gap-2 overflow-x-auto no-scrollbar text-[11px]"
            >
              <template
                v-for="(nav, idx) in activeNavItems"
                :key="idx"
              >
                <span
                  class="px-2.5 py-1 rounded-lg font-semibold shrink-0 transition"
                  :class="
                    idx === 0
                      ? 'bg-[#1b3d3a] text-[#d4af37] font-bold'
                      : 'text-white/70'
                  "
                >
                  {{ nav.label || "Link" }}
                </span>
              </template>
            </div>
          </div>

          <!-- Quick Stats Box -->
          <div
            class="rounded-2xl bg-gradient-to-br from-[#0f2423] to-[#163533] p-5 text-white shadow-md space-y-3"
          >
            <div
              class="flex items-center justify-between text-xs text-[#d4af37] font-bold"
            >
              <span>CONFIG STATUS</span>
              <span>{{ activeNavItems.length }} Links Active</span>
            </div>
            <p class="text-xs text-slate-300 leading-relaxed">
              Your modifications update in real time. Hit
              <strong class="text-white">Publish Header</strong> to apply your
              settings across the Educanium PWA.
            </p>
          </div>
        </div>

        <!-- RIGHT COLUMN: MODULAR CONFIGURATION BUILDER (7 Cols on LG) -->
        <div class="lg:col-span-7 space-y-6">
          <!-- TAB / SECTION SELECTOR BUTTONS -->
          <div class="grid grid-cols-3 gap-2 bg-slate-200/70 p-1.5 rounded-2xl">
            <button
              type="button"
              class="py-2.5 px-3 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2"
              :class="
                activeTab === 'branding'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="activeTab = 'branding'"
            >
              <SchoolIcon class="h-3.5 w-3.5 text-indigo-600" />
              <span>Branding & Meta</span>
            </button>
            <button
              type="button"
              class="py-2.5 px-3 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2"
              :class="
                activeTab === 'contact'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="activeTab = 'contact'"
            >
              <PhoneCallIcon class="h-3.5 w-3.5 text-indigo-600" />
              <span>Contact & Social</span>
            </button>
            <button
              type="button"
              class="py-2.5 px-3 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-2"
              :class="
                activeTab === 'nav'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              "
              @click="activeTab = 'nav'"
            >
              <MenuIcon class="h-3.5 w-3.5 text-indigo-600" />
              <span>Navigation Builder</span>
            </button>
          </div>

          <!-- TAB 1: BRANDING & METADATA -->
          <div
            v-show="activeTab === 'branding'"
            class="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-xs space-y-5"
          >
            <div
              class="flex items-center justify-between border-b border-slate-100 pb-3"
            >
              <h2
                class="text-sm font-extrabold text-slate-900 uppercase tracking-wider"
              >
                School Identity & Logos
              </h2>
              <span class="text-xs text-slate-400">Step 1 of 3</span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-3 gap-5 items-start">
              <div>
                <ImageUploader
                  v-model="form.logo"
                  label="School Crest / Logo"
                  :required="false"
                />
              </div>

              <div class="sm:col-span-2 space-y-4">
                <div>
                  <label
                    class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >
                    School Name <span class="text-rose-500">*</span>
                  </label>
                  <input
                    v-model="form.schoolName"
                    type="text"
                    placeholder="e.g. Vinoda Academy"
                    class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  />
                </div>

                <div>
                  <label
                    class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                    >Header Tagline</label
                  >
                  <input
                    v-model="form.tagline"
                    type="text"
                    placeholder="e.g. Join Educanium International School"
                    class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm text-slate-800 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  />
                </div>
              </div>
            </div>

            <div
              class="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-3 border-t border-slate-100"
            >
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Estd. Year</label
                >
                <input
                  v-model="form.establishmentYear"
                  type="text"
                  placeholder="2000"
                  class="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Affiliation</label
                >
                <input
                  v-model="form.boardAffiliation"
                  type="text"
                  placeholder="CBSE Affiliated"
                  class="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Affiliation No.</label
                >
                <input
                  v-model="form.affiliationNumber"
                  type="text"
                  placeholder="230045"
                  class="w-full rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <!-- TAB 2: CONTACT & SOCIALS -->
          <div
            v-show="activeTab === 'contact'"
            class="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-xs space-y-5"
          >
            <div
              class="flex items-center justify-between border-b border-slate-100 pb-3"
            >
              <h2
                class="text-sm font-extrabold text-slate-900 uppercase tracking-wider"
              >
                Top Bar Contacts & Action Links
              </h2>
              <span class="text-xs text-slate-400">Step 2 of 3</span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Primary Phone</label
                >
                <input
                  v-model="form.primaryPhone"
                  type="text"
                  placeholder="+918638803228"
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Official Email</label
                >
                <input
                  v-model="form.email"
                  type="email"
                  placeholder="info@school.com"
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Office Desk Hours</label
                >
                <input
                  v-model="form.officeHours"
                  type="text"
                  placeholder="Mon–Sat · 8:00 AM–3:00 PM"
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >City / Location</label
                >
                <input
                  v-model="form.city"
                  type="text"
                  placeholder="Tezpur"
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Facebook URL</label
                >
                <input
                  v-model="form.facebook"
                  type="text"
                  placeholder="https://facebook.com/..."
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >YouTube URL</label
                >
                <input
                  v-model="form.youtube"
                  type="text"
                  placeholder="https://youtube.com/..."
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Portal Link URL</label
                >
                <input
                  v-model="form.portalLink"
                  type="text"
                  placeholder="https://portal.school.com"
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label
                  class="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1"
                  >Admission Form Link</label
                >
                <input
                  v-model="form.admissionLink"
                  type="text"
                  placeholder="https://school.nexterp.in/admission"
                  class="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>
          </div>

          <!-- TAB 3: NAVIGATION MENU BUILDER -->
          <div
            v-show="activeTab === 'nav'"
            class="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-xs space-y-5"
          >
            <div
              class="flex items-center justify-between border-b border-slate-100 pb-3"
            >
              <div>
                <h2
                  class="text-sm font-extrabold text-slate-900 uppercase tracking-wider"
                >
                  Navigation Menu Manager
                </h2>
                <p class="text-xs text-slate-500 mt-0.5">
                  Rearrange, toggle, or add custom items to your bottom
                  navigation bar.
                </p>
              </div>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-xl bg-indigo-50 px-3.5 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition active:scale-95 cursor-pointer border border-indigo-200/60"
                @click="addNavItem"
              >
                <PlusIcon class="h-3.5 w-3.5" />
                <span>Add Item</span>
              </button>
            </div>

            <!-- List of Nav items -->
            <div class="space-y-3 max-h-[500px] overflow-y-auto pr-1">
              <div
                v-for="(item, index) in form.navItems"
                :key="index"
                class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3.5 rounded-2xl border transition-all"
                :class="
                  item.enabled !== false
                    ? 'border-slate-200 bg-slate-50/60'
                    : 'border-slate-200/60 bg-slate-100/60 opacity-60'
                "
              >
                <div
                  class="flex items-center justify-between sm:justify-start gap-3"
                >
                  <span class="text-xs font-mono font-bold text-slate-400"
                    >#{{ index + 1 }}</span
                  >
                  <button
                    type="button"
                    class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                    :class="
                      item.enabled !== false ? 'bg-emerald-600' : 'bg-slate-300'
                    "
                    @click="item.enabled = item.enabled === false"
                  >
                    <span
                      class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition duration-200 ease-in-out"
                      :class="
                        item.enabled !== false
                          ? 'translate-x-5'
                          : 'translate-x-0'
                      "
                    />
                  </button>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 flex-1">
                  <input
                    v-model="item.label"
                    type="text"
                    placeholder="Label"
                    class="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-800 focus:border-indigo-500 focus:outline-none"
                  />
                  <div class="relative">
                    <input
                      v-model="item.url"
                      type="text"
                      placeholder="Anchor Link"
                      :disabled="isDefaultNavItem(item.label)"
                      class="w-full rounded-xl border border-slate-200 px-3 py-1.5 text-xs text-slate-700 focus:border-indigo-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                <div class="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    :disabled="index === 0"
                    class="p-1.5 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-30 cursor-pointer transition active:scale-95"
                    @click="moveNavItem(index, -1)"
                  >
                    <ArrowUpIcon class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    :disabled="index === form.navItems.length - 1"
                    class="p-1.5 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-30 cursor-pointer transition active:scale-95"
                    @click="moveNavItem(index, 1)"
                  >
                    <ArrowDownIcon class="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-xl border border-slate-200 bg-white text-rose-500 hover:text-rose-700 hover:bg-rose-50 cursor-pointer transition active:scale-95"
                    @click="removeNavItem(index)"
                  >
                    <Trash2Icon class="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Bottom Floating Save Bar for convenience -->
          <div
            class="flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs"
          >
            <div class="flex items-center gap-2 text-xs font-medium">
              <span
                v-if="hasUnsavedChanges"
                class="inline-flex items-center gap-1.5 text-amber-600 font-bold"
              >
                <span
                  class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
                ></span>
                You have unsaved edits
              </span>
              <span
                v-else
                class="inline-flex items-center gap-1.5 text-emerald-600 font-bold"
              >
                <CheckCircleIcon class="h-4 w-4" />
                Synced with server state
              </span>
            </div>

            <button
              type="button"
              class="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md transition active:scale-95 cursor-pointer disabled:opacity-50"
              :disabled="isSaving"
              @click="handleSave"
            >
              {{ isSaving ? "Saving..." : "Save Configuration" }}
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-6 right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-3xl bg-white p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all fields back to their last saved
          state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    School as SchoolIcon,
    PhoneCall as PhoneCallIcon,
    Menu as MenuIcon,
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    ArrowUp as ArrowUpIcon,
    ArrowDown as ArrowDownIcon,
    MapPin as MapPinIcon,
    Phone as PhoneIcon,
    Mail as MailIcon,
    Facebook as FacebookIcon,
    Youtube as YoutubeIcon,
    Eye as EyeIcon,
  } from "lucide-vue-next";

  import ImageUploader from "@/components/WEB/ImageUploader.vue";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);
  const activeTab = ref("branding");

  const defaultNavItems = [
    { label: "Home", url: "#", enabled: true, sortOrder: 0 },
    { label: "About", url: "#about", enabled: true, sortOrder: 1 },
    { label: "Academics", url: "#academics", enabled: true, sortOrder: 2 },
    { label: "Admissions", url: "#admissions", enabled: true, sortOrder: 3 },
    { label: "Facilities", url: "#facilities", enabled: true, sortOrder: 4 },
  ];

  const defaultMenuLabels = [
    "home",
    "about",
    "academics",
    "admission",
    "admissions",
    "facilities",
  ];

  function isDefaultNavItem(label) {
    if (!label) return false;
    return defaultMenuLabels.includes(label.trim().toLowerCase());
  }

  const form = reactive({
    logo: "",
    schoolName: "Vinoda Academy",
    tagline: "Join Educanium International School",
    establishmentYear: "2000",
    boardAffiliation: "CBSE Affiliated",
    affiliationNumber: "230045",
    officeHours: "Mon–Sat · 8:00 AM–3:00 PM",
    primaryPhone: "+918638803228",
    email: "info@school.com",
    city: "tezpur",
    state: "Assam",
    address: "",
    facebook: "https://facebook.com",
    youtube: "https://youtube.com",
    portalLink: "https://portal.school.com",
    admissionLink: "https://school.nexterp.in/admission",
    navItems: JSON.parse(JSON.stringify(defaultNavItems)),
  });

  const initialStateJson = ref("");

  const activeNavItems = computed(() => {
    return (form.navItems || []).filter((item) => item.enabled !== false);
  });

  const schoolInitials = computed(() => {
    if (!form.schoolName) return "VA";
    return form.schoolName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  });

  const resolvedLogoUrl = computed(() => {
    return resolvedImageUrl(form.logo);
  });

  function resolvedImageUrl(media) {
    if (!media) return "";
    if (typeof media === "string") return media;
    if (media instanceof File) return URL.createObjectURL(media);
    return media.url || media.secure_url || "";
  }

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  const hasUnsavedChanges = computed(() => {
    const currentComparable = { ...form, logo: getComparableImage(form.logo) };
    return JSON.stringify(currentComparable) !== initialStateJson.value;
  });

  function getComparableImage(img) {
    if (img instanceof File) return img.name;
    if (typeof img === "object" && img !== null)
      return img.url || img.secure_url || "";
    return img || "";
  }

  function snapshotState() {
    const currentComparable = { ...form, logo: getComparableImage(form.logo) };
    initialStateJson.value = JSON.stringify(currentComparable);
  }

  function addNavItem() {
    form.navItems.push({
      label: "New Option",
      url: "#",
      enabled: true,
      sortOrder: form.navItems.length,
    });
  }

  function removeNavItem(index) {
    form.navItems.splice(index, 1);
  }

  function moveNavItem(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= form.navItems.length) return;
    const item = form.navItems.splice(index, 1)[0];
    form.navItems.splice(targetIndex, 0, item);
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details = store.state.websiteDetails || {};

      if (details.schoolName) form.schoolName = details.schoolName;
      if (details.tagline) form.tagline = details.tagline;
      if (details.logo) form.logo = details.logo;
      if (details.establishmentYear || details.foundedYear)
        form.establishmentYear =
          details.establishmentYear || details.foundedYear;
      if (details.boardAffiliation)
        form.boardAffiliation = details.boardAffiliation;
      if (details.affiliationNumber)
        form.affiliationNumber = details.affiliationNumber;
      if (details.officeHours) form.officeHours = details.officeHours;
      if (details.primaryPhone) form.primaryPhone = details.primaryPhone;
      if (details.email) form.email = details.email;
      if (details.city) form.city = details.city;
      if (details.state) form.state = details.state;
      if (details.address) form.address = details.address;
      if (details.facebook) form.facebook = details.facebook;
      if (details.youtube) form.youtube = details.youtube;
      if (details.portalLink) form.portalLink = details.portalLink;
      if (details.admissionLink) form.admissionLink = details.admissionLink;

      if (Array.isArray(details.navItems) && details.navItems.length > 0) {
        form.navItems = details.navItems.map((n, idx) => ({
          label: n.label || "",
          url: n.url || "#",
          enabled: n.enabled !== false,
          sortOrder: n.sortOrder ?? idx,
        }));
      }

      snapshotState();
    } catch (err) {
      showToast("Failed to load header details", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    if (!form.schoolName.trim()) {
      showToast("School Name is required", "error");
      return;
    }

    isSaving.value = true;
    try {
      const payload = {
        logo: form.logo,
        schoolName: form.schoolName,
        tagline: form.tagline,
        establishmentYear: form.establishmentYear,
        foundedYear: form.establishmentYear,
        boardAffiliation: form.boardAffiliation,
        affiliationNumber: form.affiliationNumber,
        officeHours: form.officeHours,
        primaryPhone: form.primaryPhone,
        email: form.email,
        city: form.city,
        state: form.state,
        address: form.address,
        facebook: form.facebook,
        youtube: form.youtube,
        portalLink: form.portalLink,
        admissionLink: form.admissionLink,
        navItems: form.navItems.map((item, idx) => ({
          ...item,
          sortOrder: idx,
        })),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      snapshotState();
      showToast("Header settings updated successfully!", "success");
    } catch (err) {
      showToast(err.message || "Error saving header configuration", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Form reset to saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>
